/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { getResource } from "../../../language/resources/getResource";
import { Column } from "../../../layout/flexbox";
import LabelGroup, { ROW_GROUP } from "../../../forms/elements/LabelGroup";
import { EntitlementType } from "../../../entitlementTypes/entitlementType";
import { AccessNode, FulfillmentSeason, SellingSeason } from "../../models";

import { useMemo, useState } from "react";
import { SingleSelectFilter } from "../../multiSelect/SingleSelectFilter";
import { DisplayField, GroupBy, ItemKey } from "../../multiSelect/types";
import { MultiSelectAdvancedFilter } from "../../multiSelect/MultiSelectAdvancedFilter";
import { MultiSelectFilter } from "../../multiSelect/MultiSelectFilter";
import {
  BLACK,
  COOLER_GREY,
  MIDDLE_GREY,
  RED_CREEK,
} from "../../../styles/colors";
import downArrowIcon from "../../../assets/icons/downArrow.svg";
import { forwardRef, useImperativeHandle } from "react";

type Props = {
  resources: Record<string, any>;
  sellingSeasons: SellingSeason[];
  fulfillmentSeasons: FulfillmentSeason[];
  entitlementTypes: EntitlementType[];
  accessNodes: any[];
  onSellingSeasonChange?: (season?: SellingSeason) => void;
};

const headerGridCss = css`
  display: grid;
  grid-template-columns: 1fr 1.25fr;
  column-gap: 32px;
  row-gap: 15px;

  margin-top: 10px;
  margin-bottom: 10px;
  margin-left: 35px;
  margin-right: 45px;
`;

const labelGroupCss = css`
  ${ROW_GROUP};
  width: 100%;

  display: grid;
  align-items: center;

  label {
    white-space: nowrap;
  }
`;

const labelGroupLeftCss = css`
  ${labelGroupCss}
  grid-template-columns: 125px 1fr; /* label column + input column */
`;

const labelGroupRightCss = css`
  ${labelGroupCss}
  grid-template-columns: 195px 1fr; /* label column + input column */
`;

const multiSelectButtonCss = css`
  border: 1px solid ${MIDDLE_GREY}; /* thinner */
  border-radius: 4px;

  &:hover {
    border-color: ${MIDDLE_GREY};
  }

  /* Inset, so the 2px look does not resize the control. */
  &:focus,
  &.is-open {
    border-color: ${BLACK};
    box-shadow: inset 0 0 0 1px ${BLACK};
  }

  /* Keyboard focus ring wins. */
  &:focus-visible {
    border-color: ${BLACK};
    box-shadow: 0 0 0 1px ${BLACK};
  }
`;

const multiSelectPlaceholder = css`
  color: ${COOLER_GREY};
  text-transform: none;
  font-size: 16px;
  font-weight: normal;
  padding: 0;
  text-align: left;
`;

/* Mutes the chevron while the placeholder shows. Opacity only -- anything affecting layout
   would move the arrow on selection. */
const iconPlaceholderCss = css`
  opacity: 0.3;
`;

const multiSelectSelctedText = css`
  color: ${BLACK};
  font-weight: normal;
  font-size: 15px;
  text-align: left;
`;

/* Tighter drop than the shared 6px. Header only. */
const panelGapCss = css`
  top: calc(100% + 2px);
`;

const errorCss = css`
  box-shadow: inset 0 0 0 2px ${RED_CREEK};
`;

export type LiftAccessRuleSetEditableHeaderHandle = {
  validate: () => boolean;
  getValue: () => {
    sellingSeason?: SellingSeason;
    fulfillmentSeason?: FulfillmentSeason;
    entitlementTypeIds: ItemKey[];
    accessNodes: AccessNode[];
  };
};

export const LiftAccessRuleSetEditableHeader = forwardRef<
  LiftAccessRuleSetEditableHeaderHandle,
  Props
>(function LiftAccessRuleSetEditableHeader(
  {
    resources,
    sellingSeasons,
    fulfillmentSeasons,
    entitlementTypes,
    accessNodes,
    onSellingSeasonChange,
  },
  ref,
) {
  const [selectedEntitlementTypeIds, setSelectedEntitlementTypeIds] = useState<
    ItemKey[]
  >([]);

  const [selectedAccessNodeIds, setSelectedAccessNodeIds] = useState<ItemKey[]>(
    [],
  );

  const [selectedSellingSeason, setSelectedSellingSeason] = useState<
    SellingSeason | undefined
  >(undefined);

  const [selectedFulfillmentSeason, setSelectedFulfillmentSeason] = useState<
    FulfillmentSeason | undefined
  >(undefined);

  const [showErrors, setShowErrors] = useState(false);

  const validation = {
    sellingSeason: !!selectedSellingSeason,
    fulfillmentSeason: !!selectedFulfillmentSeason,
    entitlementTypes: selectedEntitlementTypeIds.length > 0,
    accessNodes: selectedAccessNodeIds.length > 0,
  };

  const isValid =
    validation.sellingSeason &&
    validation.fulfillmentSeason &&
    validation.entitlementTypes &&
    validation.accessNodes;

  const selectedAccessNodes = useMemo(() => {
    const selectedSet = new Set(selectedAccessNodeIds);
    return accessNodes.filter((node) => selectedSet.has(String(node.id)));
  }, [accessNodes, selectedAccessNodeIds]);

  useImperativeHandle(ref, () => ({
    validate: () => {
      setShowErrors(true);
      return isValid;
    },
    getValue: () => ({
      sellingSeason: selectedSellingSeason,
      fulfillmentSeason: selectedFulfillmentSeason,
      entitlementTypeIds: selectedEntitlementTypeIds,
      accessNodes: selectedAccessNodes,
    }),
  }));

  const showSelectAllForEntitlementTypes =
    !!selectedSellingSeason || selectedEntitlementTypeIds.length > 0;

  const sortedSellingSeasons = useMemo(() => {
    if (!Array.isArray(sellingSeasons)) {
      return [];
    }

    return [...sellingSeasons].sort(
      (a, b) => b.sellingSeasonId - a.sellingSeasonId,
    );
  }, [sellingSeasons]);

  const sortedFulfillmentSeasons = useMemo(() => {
    if (!Array.isArray(fulfillmentSeasons)) {
      return [];
    }

    return [...fulfillmentSeasons].sort(
      (a, b) => b.fulfillmentSeasonId - a.fulfillmentSeasonId,
    );
  }, [fulfillmentSeasons]);

  const sortedEntitlementTypes = useMemo(() => {
    if (!Array.isArray(entitlementTypes)) {
      return [];
    }

    return [...entitlementTypes].sort(
      (a, b) => b.entitlementTypeId - a.entitlementTypeId,
    );
  }, [entitlementTypes]);

  const selectableEntitlementTypes = useMemo(() => {
    if (!selectedSellingSeason) {
      return sortedEntitlementTypes;
    }

    return sortedEntitlementTypes.map((et) => ({
      ...et,
      disabled:
        et.seasonDescription !== selectedSellingSeason.seasonDescription,
    }));
  }, [sortedEntitlementTypes, selectedSellingSeason]);

  const getSellingSeasonKey = (sellingSeason: SellingSeason) =>
    sellingSeason.sellingSeasonId;
  const getSellingSeasonLabel = (sellingSeason: SellingSeason) =>
    sellingSeason.seasonDescription;

  const getFulfillmentSeasonKey = (fulfillmentSeason: FulfillmentSeason) =>
    fulfillmentSeason.fulfillmentSeasonId;
  const getFulfillmentSeasonLabel = (fulfillmentSeason: FulfillmentSeason) =>
    fulfillmentSeason.seasonDescription;

  const entitlementTypeGroupBy: GroupBy<EntitlementType>[] = [
    {
      label: "Season",
      accessor: {
        kind: "field",
        field: "seasonDescription",
      },
    },
  ];

  const entitlementTypeDisplayFields: DisplayField<EntitlementType>[] = [
    {
      label: "Entitlement Type Id",
      width: 70,
      accessor: {
        kind: "field",
        field: "entitlementTypeId",
      },
    },
    {
      label: "Description",
      accessor: {
        kind: "field",
        field: "publicDescription",
      },
    },
  ];

  const onSelectSellingSeason = (sellingSeason: SellingSeason) => {
    setSelectedSellingSeason(sellingSeason);

    onSellingSeasonChange?.(sellingSeason);

    setSelectedEntitlementTypeIds((prev) =>
      prev.filter((key) => {
        const et = sortedEntitlementTypes.find(
          (x) => String(x.entitlementTypeId) === key,
        );

        return et && et.seasonDescription === sellingSeason.seasonDescription;
      }),
    );
  };

  const onSelectFulfillmentSeason = (fulFillmentSeason: FulfillmentSeason) => {
    setSelectedFulfillmentSeason(fulFillmentSeason);
  };

  const onSelectEntitlementTypes = (keys: ItemKey[]) => {
    setSelectedEntitlementTypeIds(keys);

    // If no season selected yet, set season from the first selected item
    if (!selectedSellingSeason && keys.length > 0) {
      const first = sortedEntitlementTypes.find(
        (et) => String(et.entitlementTypeId) === keys[0],
      );

      if (first) {
        const derivedSeason = sellingSeasons.find(
          (s) => s.seasonDescription === first.seasonDescription,
        );

        if (derivedSeason) {
          setSelectedSellingSeason(derivedSeason);
        }
      }
    }
  };

  return (
    <Column rootCss={headerGridCss}>
      {/* Row 1 */}
      <LabelGroup
        label={getResource(resources, "ACCESS_RULE_EDIT_SEASON")}
        title={getResource(resources, "ACCESS_RULE_EDIT_SELLING_SEASON")}
        rootCss={labelGroupLeftCss}
      >
        <SingleSelectFilter<SellingSeason>
          resources={resources}
          items={sortedSellingSeasons}
          selectedItem={selectedSellingSeason}
          onChange={onSelectSellingSeason}
          placeholder={getResource(resources, "ACCESS_RULE_PLEASE_SELECT")}
          noItemsMessage={getResource(
            resources,
            "ACCESS_RULE_NO_SELLING_SEASONS",
          )}
          keyAccessor={{ kind: "get", get: getSellingSeasonKey }}
          labelAccessor={{ kind: "get", get: getSellingSeasonLabel }}
          controlWidth={"100%"}
          controlHeight={40}
          iconSize={18}
          buttonCss={css`
            ${multiSelectButtonCss};
            ${showErrors && !validation.sellingSeason && errorCss};
          `}
          panelCss={panelGapCss}
          placeholderTextCss={multiSelectPlaceholder}
          selectedTextCss={multiSelectSelctedText}
          iconCss={iconPlaceholderCss}
          dropdownIconSrc={downArrowIcon}
        />
      </LabelGroup>

      <LabelGroup
        label={getResource(resources, "ACCESS_RULE_EDIT_FULFILLMENT_SEASON")}
        title={getResource(resources, "ACCESS_RULE_EDIT_FULFILLMENT_SEASON")}
        rootCss={labelGroupRightCss}
      >
        <SingleSelectFilter<FulfillmentSeason>
          resources={resources}
          items={sortedFulfillmentSeasons}
          selectedItem={selectedFulfillmentSeason}
          onChange={onSelectFulfillmentSeason}
          placeholder={getResource(resources, "ACCESS_RULE_PLEASE_SELECT")}
          noItemsMessage={getResource(
            resources,
            "ACCESS_RULE_NO_FULFILLMENT_SEASONS",
          )}
          keyAccessor={{ kind: "get", get: getFulfillmentSeasonKey }}
          labelAccessor={{ kind: "get", get: getFulfillmentSeasonLabel }}
          controlWidth={"100%"}
          controlHeight={40}
          iconSize={18}
          buttonCss={css`
            ${multiSelectButtonCss};
            ${showErrors && !validation.fulfillmentSeason && errorCss};
          `}
          panelCss={panelGapCss}
          placeholderTextCss={multiSelectPlaceholder}
          selectedTextCss={multiSelectSelctedText}
          iconCss={iconPlaceholderCss}
          dropdownIconSrc={downArrowIcon}
        />
      </LabelGroup>

      {/* Row 2 */}
      <LabelGroup
        label={getResource(resources, "ACCESS_RULE_ADD_ENTITELEMENT")}
        title={getResource(resources, "ACCESS_RULE_ADD_ENTITLEMENT_TYPES")}
        rootCss={css`
          ${labelGroupLeftCss};
          grid-column: 1 / span 2;
        `}
      >
        <MultiSelectAdvancedFilter
          resources={resources}
          label={getResource(resources, "ACCESS_RULE_PLEASE_SELECT")}
          clearLabel={getResource(resources, "ACCESS_OVERVIEW_CLEAR")}
          searchPlaceholder={getResource(
            resources,
            "ACCESS_OVERVIEW_SEARCH_PLACEHOLDER",
          )}
          items={selectableEntitlementTypes}
          keyAccessor={{
            kind: "get",
            get: (et) => String(et.entitlementTypeId),
          }}
          displayFields={entitlementTypeDisplayFields}
          groupBy={entitlementTypeGroupBy}
          selectedKeys={selectedEntitlementTypeIds}
          onChange={onSelectEntitlementTypes}
          showSearch
          searchInControl
          showClear
          showSelectAll={showSelectAllForEntitlementTypes}
          controlWidth={"100%"}
          panelMaxWidth={700}
          panelMaxHeight={600}
          iconSize={18}
          controlHeight={40}
          buttonCss={css`
            ${multiSelectButtonCss};
            ${showErrors && !validation.entitlementTypes && errorCss};
          `}
          panelCss={panelGapCss}
          placeholderTextCss={multiSelectPlaceholder}
          selectedTextCss={multiSelectSelctedText}
          iconCss={iconPlaceholderCss}
          dropdownIconSrc={downArrowIcon}
          isItemDisabled={(et) =>
            selectedSellingSeason
              ? et.seasonDescription !== selectedSellingSeason.seasonDescription
              : false
          }
        />
      </LabelGroup>

      {/* Row 3 */}
      <LabelGroup
        label={getResource(resources, "ACCESS_RULE_EDIT_LOCATION")}
        title={getResource(resources, "ACCESS_RULE_EDIT_LOCATION")}
        rootCss={labelGroupLeftCss}
      >
        <MultiSelectFilter
          resources={resources}
          label={getResource(resources, "ACCESS_RULE_PLEASE_SELECT")}
          clearLabel={getResource(resources, "ACCESS_OVERVIEW_CLEAR")}
          searchPlaceholder={getResource(
            resources,
            "ACCESS_OVERVIEW_SEARCH_PLACEHOLDER",
          )}
          items={accessNodes}
          keyAccessor={{
            kind: "get",
            get: (p) => String(p.id),
          }}
          labelAccessor={{
            kind: "field",
            field: "name",
          }}
          selectedKeys={selectedAccessNodeIds}
          onChange={setSelectedAccessNodeIds}
          showSearch
          searchInControl
          showClear
          iconSize={18}
          controlWidth="100%"
          panelMaxWidth={450}
          panelMaxHeight={550}
          controlHeight={40}
          buttonCss={css`
            ${multiSelectButtonCss};
            ${showErrors && !validation.accessNodes && errorCss};
          `}
          panelCss={panelGapCss}
          placeholderTextCss={multiSelectPlaceholder}
          selectedTextCss={multiSelectSelctedText}
          iconCss={iconPlaceholderCss}
          dropdownIconSrc={downArrowIcon}
        />
      </LabelGroup>
      <div />
    </Column>
  );
});
